import React, { useState, useCallback } from "react";
import Papa from "papaparse";
import { Upload, AlertCircle } from 'lucide-react';
import { colors, typography, spacing, components, transitions } from '../styles/shared';

const styles = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: spacing[6],
  },
  title: {
    ...typography.h3,
    color: colors.gray[900],
    margin: 0,
  },
  description: {
    ...typography.body1,
    color: colors.gray[600],
    margin: 0,
    maxWidth: '600px',
  },
  uploadArea: {
    border: `2px dashed ${colors.gray[300]}`,
    borderRadius: components.card.borderRadius,
    padding: spacing[12],
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: spacing[4],
    cursor: 'pointer',
    transition: transitions.smooth,
    backgroundColor: colors.background.subtle,
    '&:hover': {
      borderColor: colors.primary[400],
      backgroundColor: colors.primary[50],
    },
  },
  uploadAreaDisabled: {
    opacity: 0.6,
    cursor: 'not-allowed',
    '&:hover': {
      borderColor: colors.gray[300],
      backgroundColor: colors.background.subtle,
    },
  },
  uploadIcon: {
    width: '48px',
    height: '48px',
    color: colors.primary[600],
    opacity: 0.9,
  },
  uploadText: {
    ...typography.body1,
    color: colors.gray[600],
    textAlign: 'center',
  },
  uploadTextStrong: {
    color: colors.gray[900],
    fontWeight: '500',
  },
  error: {
    display: 'flex',
    alignItems: 'center',
    gap: spacing[2],
    padding: `${spacing[3]} ${spacing[4]}`,
    backgroundColor: colors.error[50],
    borderRadius: components.card.borderRadius,
    color: colors.error[600],
    ...typography.body2,
  },
  errorIcon: {
    width: '16px',
    height: '16px',
    flexShrink: 0,
  },
  hiddenInput: {
    display: 'none',
  },
};

const UploadCSV = ({ qrType, onUpload }) => {
  const [error, setError] = useState(null);

  const handleFileUpload = useCallback((file) => {
    if (!file) {
      setError('No file selected');
      return;
    }

    if (file.type !== 'text/csv') {
      setError('Please upload a CSV file');
      return;
    }

    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        if (results.errors.length > 0) {
          setError('Error parsing CSV file');
          return;
        }

        if (results.data.length === 0) {
          setError('CSV file is empty');
          return;
        }

        const headers = Object.keys(results.data[0]);
        if (headers.length === 0) {
          setError('CSV file has no headers');
          return;
        }

        setError(null);
        onUpload(headers, results.data, file);
      },
      error: () => {
        setError('Error parsing CSV file');
      },
    });
  }, [setError, onUpload]);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    if (!qrType) return;
    
    const file = e.dataTransfer.files[0];
    if (file && file.type === 'text/csv') {
      handleFileUpload(file);
    }
  }, [qrType, handleFileUpload]);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
  }, []);

  return (
    <div style={styles.container}>
      <h2 style={styles.title}>Upload CSV File</h2>
      <p style={styles.description}>
        Upload your CSV file containing the data for QR code generation.
        Make sure your file has headers matching the required fields.
      </p>
      
      <input
        id="csvUpload"
        type="file"
        accept=".csv"
        onChange={(e) => {
          const file = e.target.files[0];
          if (file) {
            handleFileUpload(file);
          }
        }}
        disabled={!qrType}
        style={styles.hiddenInput}
      />
      
      <label
        htmlFor="csvUpload"
        style={{
          ...styles.uploadArea,
          ...(!qrType && styles.uploadAreaDisabled),
        }}
        onDrop={qrType ? handleDrop : undefined}
        onDragOver={qrType ? handleDragOver : undefined}
      >
        <Upload style={styles.uploadIcon} />
        <div style={styles.uploadText}>
          <span style={styles.uploadTextStrong}>Click to upload</span> or drag and drop
          <br />
          CSV files only
        </div>
      </label>

      {error && (
        <div style={styles.error}>
          <AlertCircle style={styles.errorIcon} />
          {error}
        </div>
      )}

      {!qrType && (
        <div style={styles.error}>
          <AlertCircle style={styles.errorIcon} />
          Please select a QR Code type first
        </div>
      )}
    </div>
  );
};

export default UploadCSV;
